import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Modal,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import deletePopupImg from "../../../assets/images/png/Group 2@2x.png";

import QuotationItemsTable from "./QuotationItemsTable";

import { API_ENDPOINT } from "../../../services/api/resources";
import styles from "../../VendorsTabs/InvoiceProcess/InvoiceProcess.module.css";
import { DOCUMENT_UPLOAD_ACCEPT } from "../../../utils/documentUploadTypes";

const STORAGE_KEY = "unregistered_vendor_quotation_extraction";

const normalizeWarranty = (w) => ({
  warranty_raw_text: w?.warranty_raw_text || "",
  warranty_number: w?.warranty_number ?? null,
  warranty_period: w?.warranty_period || "",
  warranty_type: w?.warranty_type || "",
});

const normalizeItemForSave = (item) => ({
  item_name: item?.item_name || "",
  part_number: item?.part_number || "",
  unit_price: item?.unit_price ?? null,
  total_price: item?.total_price ?? null,
  tax: item?.tax ?? null,
  serial_number: item?.serial_number || "",
  model_number: item?.model_number || "",
  engine_number: item?.engine_number || "",
  description: item?.description || "",
  warranty: normalizeWarranty(item?.warranty),
});

const flattenItems = (items = []) =>
  (Array.isArray(items) ? items : []).map((item, idx) => {
    const unit = toNumber(item?.unit_price);
    const taxPct = toNumber(item?.tax);
    const lineGst = taxPct ? (unit * taxPct) / 100 : 0;
    const lineTotal = unit + lineGst;

    return {
      token: item?.token || item?.part_number || `${idx + 1}`,
      item_name: item?.item_name || "-",
      part_number: item?.part_number || "-",
      unit_price: formatRupees(item?.unit_price ?? "-"),
      total_price: formatRupees(
        item?.total_price ?? (Number.isFinite(lineTotal) ? lineTotal : "-"),
      ),
      tax: item?.tax ?? "-",
      serial_number: item?.serial_number || "-",
      model_number: item?.model_number || "-",
      engine_number: item?.engine_number || "-",
      description: item?.description || "-",
      warranty_period: item?.warranty?.warranty_period || "-",
      warranty_type: item?.warranty?.warranty_type || "-",
      warranty_raw_text: item?.warranty?.warranty_raw_text || "-",
    };
  });

const safeLower = (value) =>
  (value === undefined || value === null ? "" : String(value)).toLowerCase();

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const normalized = String(value)
    .replace(/[, ]/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatIN = (value) =>
  Number.isFinite(value)
    ? value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "-";

const formatRupees = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") return `₹${formatIN(value)}`;

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "-") return "-";
  if (trimmed.startsWith("₹")) return trimmed;
  return `₹${trimmed}`;
};

const computeTotals = (items = []) => {
  const list = Array.isArray(items) ? items : [];
  let subtotal = 0;
  let gst = 0;
  let total = 0;

  list.forEach((it) => {
    const unit = toNumber(it?.unit_price);
    const taxPct = toNumber(it?.tax);
    const lineGst = taxPct ? (unit * taxPct) / 100 : 0;
    const lineTotal = unit + lineGst;
    subtotal += unit;
    gst += lineGst;
    total += lineTotal;
  });

  return { subtotal, gst, total };
};

const CatalogAdd = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const loginData = useSelector((state) => state.loginData.value);
  const authToken =
    loginData?.auth_token || loginData?.access_token || loginData?.token || "";

    
  const vendorToken =
    loginData?.vendor_token || loginData?.user?.token || loginData?.user?.user_token || "";
  const orgToken =
    loginData?.organization_token || loginData?.org_token || loginData?.user?.organization_token || vendorToken;

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractError, setExtractError] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [extractedReady, setExtractedReady] = useState(false);

  const [fileName, setFileName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [items, setItems] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [saveSuccessMeta, setSaveSuccessMeta] = useState({
    quotation_token: "",
    saved_count: 0,
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusContent, setStatusContent] = useState({
    title: "",
    description: "",
    icon: successPopupImg,
  });

  const [deleteExtractOpen, setDeleteExtractOpen] = useState(false);

  const quotationHeadings = useMemo(
    () => [
      { label: "Items", field: "items_count", col_width: "120px" },
      { label: "Subtotal", field: "subtotal", col_width: "160px" },
      { label: "GST", field: "gst_amount", col_width: "160px" },
      { label: "Total", field: "total_amount", col_width: "160px" },
      { label: "Extracted Date", field: "created_date", col_width: "220px" },
    ],
    [],
  );

  const itemHeadings = useMemo(
    () => [
      { label: "Item Name", field: "item_name", col_width: "280px" },
      { label: "Part No", field: "part_number", col_width: "180px" },
      { label: "Model No", field: "model_number", col_width: "160px" },
      { label: "Unit Price", field: "unit_price", col_width: "140px" },
      { label: "Tax (%)", field: "tax", col_width: "100px" },
      { label: "Total Price", field: "total_price", col_width: "160px" },
      {
        label: "Warranty",
        field: "warranty_raw_text",
        col_width: "420px",
        cellType: "readMore",
        maxLength: 90,
      },
      {
        label: "Description",
        field: "description",
        col_width: "360px",
        cellType: "readMore",
        maxLength: 90,
      },
    ],
    [],
  );

  const [viewOpen, setViewOpen] = useState(false);
  const [activeQuotation, setActiveQuotation] = useState(null);

  const quotationRow = useMemo(() => {
    if (!fileName || !extractedReady) return null;
    const itemsArr = Array.isArray(items) ? items : [];
    const totals = computeTotals(itemsArr);
    return {
      token: "extracted",
      created_date: new Date().toISOString(),
      items_count: itemsArr.length,
      subtotal: formatRupees(totals.subtotal),
      gst_amount: formatRupees(totals.gst),
      total_amount: formatRupees(totals.total),
      items: itemsArr,
    };
  }, [extractedReady, fileName, items]);

  const itemRows = useMemo(() => {
    const base = flattenItems(activeQuotation?.items || []);
    const q = safeLower(itemSearch).trim();
    if (!q) return base;
    return base.filter((row) =>
      itemHeadings.some((h) => safeLower(row[h.field]).includes(q)),
    );
  }, [activeQuotation, itemSearch, itemHeadings]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    console.log(file,"file")
    setSelectedFile(file);
    setExtractError("");
    setItems([]);
    setExtractedReady(false);
    setFileName(file?.name || "");
    setCompanyName("");
  };

  const handleClear = () => {
    setSelectedFile(null);
    setExtractError("");
    setItems([]);
    setItemSearch("");
    setExtractedReady(false);
    setFileName("");
    setCompanyName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
console.log(authToken,"unregister")
  const handleExtract = async () => {
    if (!selectedFile) return;
    setExtractError("");
    setSaveError("");
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("document", selectedFile);
      formData.append("org_token", orgToken);

      const res = await axios.post(
        API_ENDPOINT.extract_unregistered_vendor_quotation_doc,
        formData,
        {
          headers: {},
        },
      );

      const payload = res?.data || {};
      if (!payload?.success) {
        setExtractError(payload?.message || "Extraction failed.");
        setItems([]);
        setExtractedReady(false);
        return;
      }

      setFileName(payload?.file_name || selectedFile.name);
      setCompanyName(payload?.company_name || "");
      setItems(payload?.items || []);
      setExtractedReady(true);

      const cachePayload = {
        file_name: payload?.file_name || selectedFile.name,
        company_name: payload?.company_name || "",
        extracted_at: new Date().toISOString(),
        items: payload?.items || [],
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cachePayload));

      setStatusContent({
        title: "Extracted Successfully!",
        description: "Quotation data extracted. Review the items and click Save.",
        icon: successPopupImg,
      });
      setStatusOpen(true);
    } catch (err) {
      setExtractError(
        err?.response?.data?.message ||
          err?.response?.data?.response ||
          "Unable to extract quotation. Please try again.",
      );
      setItems([]);
      setExtractedReady(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaveError("");
    if (!vendorToken) {
      setSaveError("Supplier token not found. Please login again.");
      return;
    }
    if (!fileName) {
      setSaveError("File name missing. Please extract again.");
      return;
    }
    if (!Array.isArray(items) || items.length === 0) {
      setSaveError("No extracted items found. Please extract before saving.");
      return;
    }

    setSaveLoading(true);
    try {
      const normalizedItems = items.map(normalizeItemForSave);
      const formData = new FormData();
      formData.append("vendor_token", vendorToken);
      formData.append("file_name", fileName);
      formData.append("company_name", companyName || "");
      
      // Backend expects `items` as a JSON array.
      formData.append("items", JSON.stringify(normalizedItems));
      if (selectedFile) formData.append("document", selectedFile);

      const res = await axios.post(
        API_ENDPOINT.save_unregistered_vendor_quotation_doc,
        formData,
        // {
        //   headers: {
        //     ...(authToken ? { Authorization: `Token ${authToken}` } : {}),
        //   },
        // },
      );

      const responseData = res?.data || {};
      const responseMessage =
        responseData?.message || responseData?.response || "";

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          file_name: fileName,
          company_name: companyName || "",
          extracted_at: new Date().toISOString(),
          items,
          save_response: responseData,
        }),
      );

      if (
        typeof responseMessage === "string" &&
        responseMessage.toLowerCase().includes("duplicate quotation")
      ) {
        setStatusContent({
          title: "Duplicate Quotation",
          description: responseMessage,
          icon: deletePopupImg,
        });
        setStatusOpen(true);
        return;
      }

      setSaveSuccessMeta({
        quotation_token: responseData?.quotation_token || "",
        saved_count: Number(responseData?.saved_count ?? 0),
      });
      setSaveSuccessOpen(true);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.response ||
        "Unable to save quotation document. Please try again.";

      if (
        typeof errorMessage === "string" &&
        errorMessage.toLowerCase().includes("duplicate quotation")
      ) {
        setStatusContent({
          title: "Duplicate Quotation",
          description: errorMessage,
          icon: deletePopupImg,
        });
        setStatusOpen(true);
        setSaveError("");
      } else {
        setSaveError(errorMessage);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const clearExtractedData = () => {
    setItems([]);
    setItemSearch("");
    setSaveError("");
    setExtractedReady(false);
    setCompanyName("");
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <Box className={styles.container}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <HeaderTitle titleText="Add Price List" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            fontFamily: "gilroyBold",
          }}
          onClick={() => navigate("/UnregisteredVendor_dashboard/catalog")}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
              fontFamily: "gilroyMedium",
            }}
          >
            Upload File
          </Typography>

          <Box
            sx={{
              width: "min(560px, 100%)",
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#313e50",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {selectedFile ? selectedFile.name : "Click to upload file"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748b", fontSize: 14, mb: 2 }}
            >
              (Supported: .pdf, .xls, .xlsx, .doc, .docx, .xml, .csv)
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                Browse
              </Button>

              {selectedFile && (
                <Button
                  variant="text"
                  color="error"
                  size="medium"
                  sx={{
                    textTransform: "none",
                    fontSize: 14,
                    fontWeight: 500,
                    px: 2.5,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept={DOCUMENT_UPLOAD_ACCEPT}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="medium"
              disabled={!selectedFile || loading}
              onClick={handleExtract}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
                px: 3,
                fontFamily: "gilroyBold",
              }}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : "Extract"}
            </Button>

            <Button
              variant="outlined"
              size="medium"
              disabled={!selectedFile || loading}
              onClick={handleClear}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 500,
                px: 3,
              }}
            >
              Clear
            </Button>
          </Box>

          {extractError ? (
            <Typography sx={{ color: "#b91c1c", fontSize: 14 }}>
              {extractError}
            </Typography>
          ) : null}
        </Box>

        {quotationRow ? (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography sx={{ fontFamily: "gilroyBold", fontSize: 16 }}>
                  Extracted Items
                </Typography>
                {companyName ? (
                  <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                    Company: {companyName}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <DButton
                  label={"Save"}
                  btnHeight={"4rem"}
                  btnWidth={"12rem"}
                  vendorBtn={true}
                  disabled={saveLoading || loading}
                  loadingState={saveLoading}
                  onClick={handleSave}
                />
              </Box>
            </Box>

            {saveError ? (
              <Typography sx={{ color: "#b91c1c", fontSize: 14, mb: 1 }}>
                {saveError}
              </Typography>
            ) : null}

            <div>
              <DataTable
                headings={quotationHeadings}
                tableData={[quotationRow]}
                actions={true}
                viewBtn={true}
                deleteBtn={true}
                stickyFirstColumn={true}
                allowDeleteForNonAdmin={true}
                viewClick={(_, row) => {
                  setActiveQuotation(row);
                  setViewOpen(true);
                }}
                deleteClick={() => setDeleteExtractOpen(true)}
                sorting={true}
                tableSize={"100%"}
                isLoading={loading}
                errorMessage={extractError}
              />
            </div>
          </Box>
        ) : null}
      </Box>

      <Modal open={viewOpen} onClose={() => setViewOpen(false)}>
        <Fade in={viewOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(94vw, 1100px)",
              maxHeight: "88vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.2)",
              p: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 1.5,
                position: "sticky",
                top: 0,
                zIndex: 6,
                background: "#fff",
                borderBottom: "1px solid #e2e8f0",
                pb: 1,
              }}
            >
              <Box>
                <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18 }}>
                  Quotation Details
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.5 }}>
                  {Array.isArray(activeQuotation?.items)
                    ? `${activeQuotation.items.length} item(s)`
                    : "-"}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontFamily: "gilroyBold",
                  px: 3,
                  py: 1,
                  minWidth: 110,
                  fontSize: 14,
                }}
                onClick={() => setViewOpen(false)}
              >
                Close
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <SearchBar
                value={itemSearch}
                onChange={setItemSearch}
                placeholder="Search items"
              />
            </Box>

            <QuotationItemsTable
              headings={itemHeadings}
              rows={itemRows}
              onView={null}
              showActions={false}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Delete extracted data (outer table) */}
      <Modal open={deleteExtractOpen}>
        <Fade in={deleteExtractOpen}>
          <Box>
            <SuccessPopupDashboard
              popupImage={deletePopupImg}
              popupHeroText="Are you sure want to delete?"
              popupDescription={""}
              popBtn={true}
              btnText={"Delete"}
              secondaryBtn={true}
              vendorBtn={true}
              vendorBtnOutlined={true}
              btnTextSecondary={"Cancel"}
              btnClick={() => {
                clearExtractedData();
                setDeleteExtractOpen(false);
                setStatusContent({
                  title: "Deleted Successfully!",
                  description: "Extracted items cleared.",
                  icon: successPopupImg,
                });
                setStatusOpen(true);
              }}
              btnClickSecondary={() => setDeleteExtractOpen(false)}
            />
          </Box>
        </Fade>
      </Modal>

      <Modal open={statusOpen}>
        <Fade in={statusOpen}>
          <Box>
            <SuccessPopupDashboard
              popupImage={statusContent.icon}
              popupHeroText={statusContent.title}
              popupDescriptionLG={statusContent.description}
              popBtn={true}
              vendorBtn={true}
              btnText={"Okay"}
              btnClick={() => setStatusOpen(false)}
            />
          </Box>
        </Fade>
      </Modal>

      <Modal open={saveSuccessOpen}>
        <Fade in={saveSuccessOpen}>
          <Box>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText={"Saved Successfully!"}
              popupDescriptionLG={`Saved ${saveSuccessMeta.saved_count} item(s).`}
              popBtn={true}
              btnText={"Okay"}
              vendorBtn={true}
              btnClick={() => {
                setSaveSuccessOpen(false);
                navigate("/UnregisteredVendor_dashboard/catalog");
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default CatalogAdd;
